import type { LessonEnrollment } from '../api/lessons.api';

export interface FamilyContact {
  name: string;
  phone?: string;
  email?: string;
  relationship?: string;
}

/**
 * The family CONTACTS (contact1 + contact2 of every Parent row) for an enrolled
 * student, de-duplicated by name. A family is reachable by its contacts, never
 * by its logins. `phone`/`email`/`relationship` are only present when the API
 * returned them (the caller holds people_families:read).
 */
export function familyContactsFor(student: LessonEnrollment['Student']): FamilyContact[] {
  const byName = new Map<string, FamilyContact>();
  const add = (
    first: string | null | undefined,
    last: string | null | undefined,
    phone: string | null | undefined,
    email: string | null | undefined,
    relationship: string | null | undefined,
  ) => {
    const name = `${first ?? ''} ${last ?? ''}`.trim();
    if (!name) return;
    const existing = byName.get(name);
    if (existing) {
      // A second Parent row repeating the same contact may carry details the
      // first lacked — fill gaps, never overwrite.
      existing.phone ??= phone || undefined;
      existing.email ??= email || undefined;
      existing.relationship ??= relationship || undefined;
      return;
    }
    byName.set(name, {
      name,
      phone: phone || undefined,
      email: email || undefined,
      relationship: relationship || undefined,
    });
  };
  for (const p of student.Family?.Parent ?? []) {
    add(p.contact1FirstName, p.contact1LastName, p.contact1Phone, p.contact1Email, p.contact1Relationship);
    add(p.contact2FirstName, p.contact2LastName, p.contact2Phone, p.contact2Email, p.contact2Relationship);
  }
  return [...byName.values()];
}
